import { useMemo } from 'react'

import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { usePageInfo } from 'providers/PageInfoProvider'

import {
  selectCurrentVariantCohortToUse,
  selectCurrentVariantSteps,
} from 'root-redux/selects/common'
import { selectUserStatus } from 'root-redux/selects/user'

import { useBulkSelector } from 'hooks/common/useBulkSelector'
import { useUpsellStatus } from 'hooks/user/useUpsellStatus'

import { GROWTH_BOOK_FEATURE_KEYS } from 'root-constants/common'
import { PageId } from 'root-constants/pages'

const useSelectors = () =>
  useBulkSelector({
    steps: selectCurrentVariantSteps,
    userStatus: selectUserStatus,
    cohortToUse: selectCurrentVariantCohortToUse,
  })

export const useUserStatus = (): string => {
  const { steps, userStatus } = useSelectors()

  const {
    currentSubscriptionPageId,
    currentUpsellPageId,
    currentPresalePageId,
  } = usePageInfo()
  const isUpsellAvailable = useUpsellStatus()
  const isPresaleAvailable =
    !!currentPresalePageId &&
    userStatus?.email.hasEmail &&
    !userStatus?.config.presalePassed

  const isAllSetAvailable = useFeatureIsOn(
    GROWTH_BOOK_FEATURE_KEYS.car_2255_afterpay_celebration,
  )

  return useMemo(() => {
    if (userStatus?.account.hasAccount) {
      return PageId.DOWNLOAD
    }

    if (isUpsellAvailable) {
      return currentUpsellPageId
    }

    if (userStatus?.hasSubscription) {
      return isAllSetAvailable ? PageId.ALL_SET_1 : PageId.ACCOUNT
    }

    if (isPresaleAvailable) {
      return currentPresalePageId
    }

    if (userStatus?.email.hasEmail) {
      return currentSubscriptionPageId || steps[0].id
    }

    return ''
  }, [
    currentSubscriptionPageId,
    currentUpsellPageId,
    isAllSetAvailable,
    isPresaleAvailable,
    isUpsellAvailable,
    currentPresalePageId,
    steps,
    userStatus?.account.hasAccount,
    userStatus?.email.hasEmail,
    userStatus?.hasSubscription,
  ])
}
